import { useCallback, useMemo } from 'react';
import { ColumnType } from '@projectflow/shared';
import { cn } from '@/lib/utils';
import { CellLockIndicator } from '@/components/collaboration/CellLockIndicator';
import {
  TextCell,
  NumberCell,
  DateCell,
  DurationCell,
  DropdownCell,
  CheckboxCell,
  ContactCell,
  PredecessorCell,
  PercentCell,
} from './cells';

interface ColumnOptions {
  choices?: Array<{ value: string; label: string; color?: string }>;
  allowMultiple?: boolean;
  decimalPlaces?: number;
  thousandsSeparator?: boolean;
  currencyCode?: string;
  currencySymbol?: string;
  includeTime?: boolean;
  dateFormat?: string;
  durationFormat?: 'hours' | 'days' | 'weeks' | 'months';
  allowMultipleContacts?: boolean;
  showProgressBar?: boolean;
}

interface GridCellProps {
  value: unknown;
  columnType: ColumnType;
  columnOptions?: ColumnOptions;
  onChange: (value: unknown) => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
  isSelected: boolean;
  isPrimary?: boolean;
  // For contact cells
  availableContacts?: Array<{
    id: string;
    name: string;
    email: string;
    avatarUrl?: string;
  }>;
  // For predecessor cells
  availableTasks?: Array<{ id: string; rowNumber: number; name: string }>;
  currentTaskId?: string;
  // Cell lock indicator (collaboration)
  lockedBy?: { userName: string; color: string } | null;
  className?: string;
}

export function GridCell({
  value,
  columnType,
  columnOptions = {},
  onChange,
  isEditing,
  onStartEdit,
  onEndEdit,
  isSelected,
  isPrimary = false,
  availableContacts = [],
  availableTasks = [],
  currentTaskId = '',
  lockedBy,
  className,
}: GridCellProps) {
  const handleChange = useCallback(
    (newValue: unknown) => {
      onChange(newValue);
    },
    [onChange]
  );

  const cellContent = useMemo(() => {
    const commonProps = {
      isEditing,
      onStartEdit,
      onEndEdit,
      className: 'h-full',
    };

    switch (columnType) {
      case ColumnType.TEXT:
        return (
          <TextCell
            value={value as string | null}
            onChange={handleChange}
            {...commonProps}
          />
        );

      case ColumnType.NUMBER:
        return (
          <NumberCell
            value={value as number | null}
            onChange={handleChange}
            decimalPlaces={columnOptions.decimalPlaces}
            thousandsSeparator={columnOptions.thousandsSeparator}
            {...commonProps}
          />
        );

      case ColumnType.CURRENCY:
        return (
          <NumberCell
            value={value as number | null}
            onChange={handleChange}
            decimalPlaces={columnOptions.decimalPlaces ?? 2}
            thousandsSeparator={columnOptions.thousandsSeparator ?? true}
            prefix={columnOptions.currencySymbol ?? '$'}
            {...commonProps}
          />
        );

      case ColumnType.DATE:
        return (
          <DateCell
            value={value as Date | string | null}
            onChange={handleChange}
            dateFormat={columnOptions.dateFormat}
            includeTime={false}
            {...commonProps}
          />
        );

      case ColumnType.DATETIME:
        return (
          <DateCell
            value={value as Date | string | null}
            onChange={handleChange}
            dateFormat={columnOptions.dateFormat}
            includeTime={true}
            {...commonProps}
          />
        );

      case ColumnType.DURATION:
        return (
          <DurationCell
            value={value as number | null}
            onChange={handleChange}
            {...commonProps}
          />
        );

      case ColumnType.PERCENT:
        return (
          <PercentCell
            value={(value as number) ?? 0}
            onChange={handleChange}
            showProgressBar={columnOptions.showProgressBar ?? true}
            {...commonProps}
          />
        );

      case ColumnType.CHECKBOX:
        return (
          <CheckboxCell
            value={Boolean(value)}
            onChange={handleChange}
            className={commonProps.className}
          />
        );

      case ColumnType.DROPDOWN_SINGLE:
        return (
          <DropdownCell
            value={value as string | null}
            choices={columnOptions.choices ?? []}
            onChange={handleChange}
            allowMultiple={false}
            {...commonProps}
          />
        );

      case ColumnType.DROPDOWN_MULTI:
        return (
          <DropdownCell
            value={value as string[] | null}
            choices={columnOptions.choices ?? []}
            onChange={handleChange}
            allowMultiple={true}
            {...commonProps}
          />
        );

      case ColumnType.CONTACT:
        return (
          <ContactCell
            value={value as any}
            contacts={availableContacts}
            onChange={handleChange}
            allowMultiple={columnOptions.allowMultipleContacts ?? false}
            {...commonProps}
          />
        );

      case ColumnType.PREDECESSOR:
        return (
          <PredecessorCell
            value={(value as any) ?? []}
            availableTasks={availableTasks}
            onChange={handleChange}
            currentTaskId={currentTaskId}
            {...commonProps}
          />
        );

      case ColumnType.AUTO_NUMBER:
        // Auto-number is read-only
        return (
          <div className="w-full h-full px-2 py-1 text-sm text-gray-500">
            {value as number}
          </div>
        );

      case ColumnType.FORMULA:
        // Formula is read-only, display computed value
        return (
          <div className="w-full h-full px-2 py-1 text-sm text-gray-700 italic">
            {String(value ?? '')}
          </div>
        );

      case ColumnType.SYMBOL:
        // TODO: Implement symbol cell with icons
        return (
          <div className="w-full h-full px-2 py-1 text-sm">
            {String(value ?? '')}
          </div>
        );

      case ColumnType.ATTACHMENT:
        // TODO: Implement attachment cell
        return (
          <div className="w-full h-full px-2 py-1 text-sm text-gray-500">
            {Array.isArray(value) ? `${value.length} files` : ''}
          </div>
        );

      default:
        // Fallback to text
        return (
          <TextCell
            value={String(value ?? '')}
            onChange={handleChange}
            {...commonProps}
          />
        );
    }
  }, [
    columnType,
    value,
    columnOptions,
    isEditing,
    onStartEdit,
    onEndEdit,
    handleChange,
    availableContacts,
    availableTasks,
    currentTaskId,
  ]);

  return (
    <div
      className={cn(
        'relative w-full h-full min-h-[32px] cursor-default',
        isSelected && !isEditing && 'ring-2 ring-blue-500 ring-inset',
        isPrimary && 'font-medium',
        className
      )}
      onClick={() => {
        if (!isEditing) {
          onStartEdit();
        }
      }}
    >
      {cellContent}
      {lockedBy && (
        <CellLockIndicator userName={lockedBy.userName} color={lockedBy.color} />
      )}
    </div>
  );
}

export default GridCell;
